import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineSearch,
  HiOutlineDownload, HiOutlineCheckCircle, HiOutlineCurrencyRupee,
  HiOutlineClock, HiOutlineExclamation,
} from 'react-icons/hi';
import AppLayout from '../../components/layout/AppLayout';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { getPayments, createPayment, updatePayment, deletePayment, markPaymentPaid, downloadPaymentReport } from '../../api/payments';

const STATUS_COLORS = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
};

const emptyForm = {
  bill_date: '', received_date: '', due_date: '', customer_name: '', invoice_no: '',
  amount: '', status: 'pending', remarks: '',
  attachment: null, paid_to: '', payment_date: '',
};

const emptyPaidForm = { payment_date: '', paid_to: '', receipt: null };

const TODAY = new Date().toISOString().split('T')[0];

function InputField({ label, name, type = 'text', value, onChange, error, required, placeholder, accept, max }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {type === 'file' ? (
        <input
          type="file"
          name={name}
          accept={accept}
          onChange={onChange}
          className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          max={max}
          style={{
            width: '100%', padding: '10px 12px', border: `1px solid ${error ? '#EF4444' : '#E5E5E0'}`,
            borderRadius: '8px', fontSize: '14px', fontFamily: "'Lexend', sans-serif",
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, error, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        style={{
          width: '100%', padding: '10px 12px', border: `1px solid ${error ? '#EF4444' : '#E5E5E0'}`,
          borderRadius: '8px', fontSize: '14px', fontFamily: "'Lexend', sans-serif",
          outline: 'none', background: 'white', boxSizing: 'border-box',
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function SummaryCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function PaymentTrackerPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // add/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // mark paid modal
  const [paidTarget, setPaidTarget] = useState(null);
  const [paidForm, setPaidForm] = useState(emptyPaidForm);
  const [paidErrors, setPaidErrors] = useState({});
  const [paidSubmitting, setPaidSubmitting] = useState(false);

  // delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [downloading, setDownloading] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPayments();
      setPayments(Array.isArray(res) ? res : res.data || []);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const filtered = useMemo(() => {
    return payments.filter(p => {
      const kw = search.toLowerCase();
      const matchesSearch = !kw || [p.customer_name, p.invoice_no, p.remarks, p.paid_to]
        .some(v => v?.toLowerCase().includes(kw));
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const billDate = p.bill_date || '';
      const matchesFrom = !fromDate || billDate >= fromDate;
      const matchesTo = !toDate || billDate <= toDate;
      return matchesSearch && matchesStatus && matchesFrom && matchesTo;
    });
  }, [payments, search, fromDate, toDate, statusFilter]);

  const summary = useMemo(() => {
    const total = filtered.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const paid = filtered.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const pending = filtered.filter(p => p.status === 'pending').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const overdue = filtered.filter(p => p.status === 'overdue').length;
    return { total, paid, pending, overdue, count: filtered.length };
  }, [filtered]);

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const dueSoonAlerts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return payments.filter(p => {
      if (p.status === 'paid' || !p.due_date) return false;
      const due = new Date(p.due_date);
      const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 5;
    }).map(p => {
      const due = new Date(p.due_date);
      const diffDays = Math.ceil((due - new Date().setHours(0,0,0,0)) / (1000 * 60 * 60 * 24));
      return { ...p, daysLeft: diffDays };
    });
  }, [payments]);

  // Form handlers
  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      bill_date: p.bill_date || '', received_date: p.received_date || '',
      due_date: p.due_date || '', customer_name: p.customer_name || '',
      invoice_no: p.invoice_no || '', amount: p.amount || '',
      status: p.status || 'pending', remarks: p.remarks || '',
      attachment: null, paid_to: '', payment_date: '',
    });
    setErrors({});
    setShowModal(true);
  };

  const handleChange = (e) => {
    if (e.target.type === 'file') {
      setForm(f => ({ ...f, [e.target.name]: e.target.files[0] || null }));
    } else {
      setForm(f => ({ ...f, [e.target.name]: e.target.value }));
      setErrors(err => ({ ...err, [e.target.name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      if (editing) {
        const fd = new FormData();
        ['bill_date', 'received_date', 'due_date', 'customer_name', 'invoice_no', 'amount', 'status', 'remarks'].forEach(k => {
          if (form[k] !== '' && form[k] != null) fd.append(k, form[k]);
        });
        if (form.attachment instanceof File) fd.append('attachment', form.attachment);
        if (form.receipt instanceof File) fd.append('receipt', form.receipt);
        fd.append('_method', 'PUT');
        await updatePayment(editing.id, fd);
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (v instanceof File) {
            fd.append(k, v);
          } else if (v !== '' && v != null) {
            fd.append(k, v);
          }
        });
        await createPayment(fd);
      }
      setShowModal(false);
      fetchPayments();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Mark paid
  const openMarkPaid = (p) => {
    setPaidTarget(p);
    setPaidForm({ payment_date: new Date().toISOString().split('T')[0], attachment: null });
    setPaidErrors({});
  };

  const handlePaidChange = (e) => {
    if (e.target.type === 'file') {
      setPaidForm(f => ({ ...f, [e.target.name]: e.target.files[0] || null }));
    } else {
      setPaidForm(f => ({ ...f, [e.target.name]: e.target.value }));
      setPaidErrors(err => ({ ...err, [e.target.name]: '' }));
    }
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    if (!paidForm.payment_date) {
      setPaidErrors({ payment_date: 'Payment date is required' });
      return;
    }
    setPaidSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('payment_date', paidForm.payment_date);
      if (paidForm.paid_to) fd.append('paid_to', paidForm.paid_to);
      if (paidForm.receipt) fd.append('receipt', paidForm.receipt);
      await markPaymentPaid(paidTarget.id, fd);
      setPaidTarget(null);
      fetchPayments();
    } catch (err) {
      if (err.response?.status === 422) setPaidErrors(err.response.data.errors || {});
    } finally {
      setPaidSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePayment(deleteTarget.id);
      setDeleteTarget(null);
      fetchPayments();
    } catch {
    } finally {
      setDeleting(false);
    }
  };

  // Download
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadPaymentReport({ search, from_date: fromDate, to_date: toDate, status: statusFilter });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AppLayout title="Payment Tracker" breadcrumbs="Accounts / Payment Tracker">
      <div className="space-y-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard label="Total Invoices" value={summary.count} sub={fmt(summary.total)} color="bg-blue-50 text-blue-600" icon={HiOutlineCurrencyRupee} />
          <SummaryCard label="Paid" value={fmt(summary.paid)} sub={`${filtered.filter(p => p.status === 'paid').length} invoices`} color="bg-green-50 text-green-600" icon={HiOutlineCheckCircle} />
          <SummaryCard label="Pending" value={fmt(summary.pending)} sub={`${filtered.filter(p => p.status === 'pending').length} invoices`} color="bg-yellow-50 text-yellow-600" icon={HiOutlineClock} />
          <SummaryCard label="Overdue" value={summary.overdue} sub="invoices" color="bg-red-50 text-red-600" icon={HiOutlineExclamation} />
        </div>

        {/* Due Soon Alerts */}
        {dueSoonAlerts.length > 0 && (
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <HiOutlineExclamation className="w-5 h-5 text-orange-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-orange-800">
                {dueSoonAlerts.length} payment{dueSoonAlerts.length > 1 ? 's' : ''} due within 5 days
              </span>
            </div>
            {dueSoonAlerts.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-orange-100">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-gray-900">{p.customer_name}</span>
                  {p.invoice_no && <span className="text-gray-400">· {p.invoice_no}</span>}
                  <span className="font-semibold text-gray-900">{fmt(p.amount)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.daysLeft === 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                    {p.daysLeft === 0 ? 'Due Today' : `${p.daysLeft} day${p.daysLeft > 1 ? 's' : ''} left`}
                  </span>
                  <button
                    onClick={() => openMarkPaid(p)}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                  >
                    Mark Paid
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters + Actions */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, invoice, remarks..."
                style={{
                  width: '100%', paddingLeft: '36px', paddingRight: '12px', paddingTop: '10px', paddingBottom: '10px',
                  border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '14px',
                  fontFamily: "'Lexend', sans-serif", outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Date range */}
            <div className="flex gap-2 items-center">
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                style={{ padding: '10px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }} />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                style={{ padding: '10px 10px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none' }} />
            </div>

            {/* Status filter */}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '13px', fontFamily: "'Lexend', sans-serif", outline: 'none', background: 'white' }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <HiOutlineDownload className="w-4 h-4" />
                {downloading ? 'Downloading...' : 'Export'}
              </button>
              <Button onClick={openCreate}>
                <HiOutlinePlus className="w-4 h-4 mr-1 inline" />
                Add Payment
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading payments...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No payments found</div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Invoice No</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Bill Date</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Marked By</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Remarks</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                      <td className="px-5 py-4 text-sm font-medium text-gray-900">{p.invoice_no || '-'}</td>
                      <td className="px-5 py-4 text-sm text-gray-700">{p.customer_name}</td>
                      <td className="px-5 py-4 text-sm text-gray-500">{p.bill_date || '-'}</td>
                      <td className="px-5 py-4 text-sm text-gray-500">{p.due_date || '-'}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-gray-900 text-right">{fmt(p.amount)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-400">
                        {p.marked_by_name ? (
                          <div>
                            <div className="text-gray-600 font-medium">{p.marked_by_name}</div>
                            {p.payment_date && <div>{p.payment_date}</div>}
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-400 max-w-[140px] truncate">{p.remarks || '-'}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {p.status !== 'paid' && (
                            <button
                              onClick={() => openMarkPaid(p)}
                              className="px-2.5 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors border border-green-200"
                            >
                              Mark Paid
                            </button>
                          )}
                          {p.attachment_path ? (
                            <button
                              onClick={() => window.open(p.attachment_path, '_blank')}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                              title="View Invoice"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                              </svg>
                            </button>
                          ) : (
                            <span className="w-7" />
                          )}
                          {p.status === 'paid' && p.receipt_path && (
                            <button
                              onClick={() => window.open(p.receipt_path, '_blank')}
                              className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                            >
                              Receipt
                            </button>
                          )}
                          {p.status !== 'paid' && (
                            <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                              <HiOutlinePencil className="w-4 h-4" />
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button onClick={() => setDeleteTarget(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500">
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/30 text-xs text-gray-400">
                Showing {filtered.length} of {payments.length} records
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {filtered.map(p => (
                <div key={p.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{p.customer_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{p.invoice_no}</p>
                    </div>
                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize flex-shrink-0 ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Amount</span>
                      <span className="font-semibold text-gray-900">{fmt(p.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Bill Date</span>
                      <span className="text-gray-600">{p.bill_date || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Due Date</span>
                      <span className="text-gray-600">{p.due_date || '-'}</span>
                    </div>
                    {p.remarks && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Remarks</span>
                        <span className="text-gray-600 text-right max-w-[180px]">{p.remarks}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end gap-2 flex-wrap">
                    {p.status !== 'paid' && (
                      <button onClick={() => openMarkPaid(p)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-50 text-green-700 border border-green-200">
                        Mark Paid
                      </button>
                    )}
                    {p.attachment_path && (
                      <button onClick={() => window.open(p.attachment_path, '_blank')}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 border border-red-100 flex items-center gap-1">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        Invoice
                      </button>
                    )}
                    {p.status === 'paid' && p.receipt_path && (
                      <button onClick={() => window.open(p.receipt_path, '_blank')}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                        Receipt
                      </button>
                    )}
                    {p.status !== 'paid' && (
                      <button onClick={() => openEdit(p)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-600">Edit</button>
                    )}
                    {isSuperAdmin && (
                      <button onClick={() => setDeleteTarget(p)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600">Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit Payment' : 'Add Payment'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <InputField label="Bill Date" name="bill_date" type="date" value={form.bill_date} onChange={handleChange} error={errors.bill_date?.[0]} required max={TODAY} />
            <InputField label="Invoice Received Date" name="received_date" type="date" value={form.received_date} onChange={handleChange} error={errors.received_date?.[0]} max={TODAY} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <InputField label="Due Date" name="due_date" type="date" value={form.due_date} onChange={handleChange} error={errors.due_date?.[0]} />
            <InputField label="Invoice No" name="invoice_no" value={form.invoice_no} onChange={handleChange} error={errors.invoice_no?.[0]} placeholder="e.g. INV-001" />
          </div>
          <InputField label="Customer Name" name="customer_name" value={form.customer_name} onChange={handleChange} error={errors.customer_name?.[0]} required placeholder="Enter customer name" />
          <div className="grid grid-cols-2 gap-4">
            <InputField label="Amount (₹)" name="amount" type="number" value={form.amount} onChange={handleChange} error={errors.amount?.[0]} required placeholder="0.00" />
            <SelectField label="Status" name="status" value={form.status} onChange={handleChange} error={errors.status?.[0]} required
              options={editing
                ? [{ value: 'pending', label: 'Pending' }, { value: 'overdue', label: 'Overdue' }]
                : [{ value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }]
              } />
          </div>
          <InputField label="Remarks" name="remarks" value={form.remarks} onChange={handleChange} error={errors.remarks?.[0]} placeholder="Optional notes" />

          {/* Invoice Attachment — shown in both add and edit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Attachment <span className="text-gray-400 font-normal">(PDF, JPG, PNG)</span>
            </label>
            <input
              type="file"
              name="attachment"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleChange}
              className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {editing?.attachment_path && (
              <a href={editing.attachment_path} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 hover:underline mt-1 inline-block">
                View existing attachment
              </a>
            )}
          </div>

          {!editing && form.status === 'paid' && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment Details</p>
              <div className="grid grid-cols-2 gap-4">
                <InputField label="Paid To" name="paid_to" value={form.paid_to} onChange={handleChange} error={errors.paid_to?.[0]} placeholder="Recipient name" />
                <InputField label="Payment Date" name="payment_date" type="date" value={form.payment_date} onChange={handleChange} error={errors.payment_date?.[0]} />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt <span className="text-gray-400 font-normal">(PDF, JPG, PNG)</span>
                </label>
                <input
                  type="file"
                  name="receipt"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleChange}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editing ? 'Update' : 'Save Payment'}</Button>
          </div>
        </form>
      </Modal>

      {/* Mark Paid Modal */}
      <Modal isOpen={!!paidTarget} onClose={() => setPaidTarget(null)} title="Mark as Paid" size="sm">
        <form onSubmit={handleMarkPaid} className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <span className="font-medium text-gray-900">{paidTarget?.customer_name}</span>
            {paidTarget?.invoice_no && <span className="text-gray-400 ml-2">· {paidTarget.invoice_no}</span>}
            <div className="text-lg font-bold text-gray-900 mt-1">{paidTarget ? fmt(paidTarget.amount) : ''}</div>
          </div>
          <InputField
            label="Payment Date"
            name="payment_date"
            type="date"
            value={paidForm.payment_date}
            onChange={handlePaidChange}
            error={paidErrors.payment_date}
            required
          />
          <InputField
            label="Paid To"
            name="paid_to"
            value={paidForm.paid_to}
            onChange={handlePaidChange}
            error={paidErrors.paid_to}
            placeholder="Recipient name"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Receipt <span className="text-gray-400 font-normal">(PDF, JPG, PNG — optional)</span>
            </label>
            <input
              type="file"
              name="receipt"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handlePaidChange}
              className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" type="button" onClick={() => setPaidTarget(null)}>Cancel</Button>
            <Button className="flex-1" type="submit" loading={paidSubmitting}>Confirm Payment</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Payment" size="sm">
        <div className="text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <HiOutlineTrash className="w-7 h-7 text-red-500" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Are you sure you want to delete this payment?</p>
            <p className="font-semibold text-gray-900 mt-1">{deleteTarget?.customer_name} · {deleteTarget?.invoice_no}</p>
            <p className="text-xs text-gray-400 mt-1">This action cannot be undone.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} loading={deleting}>Delete</Button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}
